import type {
  BillingType,
  ClientPaymentStatus,
  JobPaymentSummary,
  JobWithPaymentSummary,
  PaymentFlowCode,
  PaymentMethod,
} from '@/types/jobPaymentTypes';

const PAID_JOB_STATUSES = new Set([
  'confirmed',
  'worker_on_way',
  'reached',
  'in_progress',
  'job_finished',
  'completed',
  'successfully_completed',
]);

function asJob(job: unknown): JobWithPaymentSummary | null {
  if (!job || typeof job !== 'object') return null;
  return job as JobWithPaymentSummary;
}

function normalizeBillingType(value: unknown): BillingType | undefined {
  if (value === 'fixed' || value === 'hourly') return value;
  return undefined;
}

function normalizePaymentMethod(value: unknown): PaymentMethod | null {
  if (value === 'STRIPE' || value === 'ATH') return value;
  return null;
}

function inferClientPaymentMethod(job: JobWithPaymentSummary): PaymentMethod | null {
  const explicit =
    normalizePaymentMethod(job.clientPaymentMethod) ??
    normalizePaymentMethod(job.paymentSummary?.clientPaymentMethod);
  if (explicit) return explicit;
  if (job.paymentIntentId) return 'STRIPE';
  if (job.athEcommerceId) return 'ATH';
  if (job.paymentSummary?.hasStripePayment) return 'STRIPE';
  if (job.paymentSummary?.hasAthPayment) return 'ATH';
  return null;
}

function inferWorkerPaymentMethod(job: JobWithPaymentSummary): PaymentMethod | null {
  const explicit =
    normalizePaymentMethod(job.workerPaymentMethod) ??
    normalizePaymentMethod(job.paymentSummary?.workerPaymentMethod);
  if (explicit) return explicit;
  return normalizePaymentMethod(job.assignedWorker?.workerProfile?.paymentType);
}

function inferClientPaymentStatus(
  job: JobWithPaymentSummary,
  clientMethod: PaymentMethod | null
): ClientPaymentStatus {
  const fromSummary = job.paymentSummary?.clientPaymentStatus;
  if (fromSummary === 'unpaid' || fromSummary === 'pending' || fromSummary === 'confirmed') {
    return fromSummary;
  }
  if (job.athEcommerceId && job.status === 'awaiting_payment') return 'pending';
  if (clientMethod && PAID_JOB_STATUSES.has(job.status || '')) return 'confirmed';
  if (clientMethod) return 'confirmed';
  return 'unpaid';
}

function inferPaymentFlow(
  clientMethod: PaymentMethod | null,
  workerMethod: PaymentMethod | null,
  existing?: PaymentFlowCode
): PaymentFlowCode {
  if (existing && existing !== 'UNKNOWN') return existing;
  if (clientMethod === 'STRIPE' && workerMethod === 'STRIPE') return 'STRIPE_TO_STRIPE';
  if (clientMethod === 'STRIPE' && workerMethod === 'ATH') return 'STRIPE_TO_ATH';
  if (clientMethod === 'ATH' && workerMethod === 'STRIPE') return 'ATH_TO_STRIPE';
  if (clientMethod === 'ATH' && workerMethod === 'ATH') return 'ATH_TO_ATH';
  return 'UNKNOWN';
}

export function getPaymentSummary(job: unknown): JobPaymentSummary {
  const j = asJob(job);
  if (!j) {
    return {
      clientPaymentMethod: null,
      workerPaymentMethod: null,
      paymentFlow: 'UNKNOWN',
      clientPaymentStatus: 'unpaid',
      hasStripePayment: false,
      hasAthPayment: false,
    };
  }

  const existing = j.paymentSummary;
  const clientPaymentMethod = inferClientPaymentMethod(j);
  const workerPaymentMethod = inferWorkerPaymentMethod(j);
  const billingType =
    normalizeBillingType(existing?.paymentType) ?? normalizeBillingType(j.paymentType);

  return {
    paymentType: billingType,
    hourlyRate: existing?.hourlyRate ?? j.hourlyRate,
    totalHours: existing?.totalHours ?? j.totalHours,
    clientPaymentMethod,
    workerPaymentMethod,
    paymentFlow: inferPaymentFlow(
      clientPaymentMethod,
      workerPaymentMethod,
      existing?.paymentFlow
    ),
    clientPaymentStatus: inferClientPaymentStatus(j, clientPaymentMethod),
    hasStripePayment:
      existing?.hasStripePayment ??
      (Boolean(j.paymentIntentId) || clientPaymentMethod === 'STRIPE'),
    hasAthPayment:
      existing?.hasAthPayment ??
      (Boolean(j.athEcommerceId) || clientPaymentMethod === 'ATH'),
  };
}

export function extractPaymentSummaryFromResponse(
  response: unknown
): JobPaymentSummary | undefined {
  if (!response || typeof response !== 'object') return undefined;
  const r = response as Record<string, unknown>;
  const data = r.data as Record<string, unknown> | undefined;
  const raw = (r.paymentSummary ?? data?.paymentSummary) as Partial<JobPaymentSummary> | undefined;
  if (!raw || typeof raw !== 'object') return undefined;
  return getPaymentSummary({ ...r, paymentSummary: raw });
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export function formatPaymentMethod(
  method: PaymentMethod | null | undefined,
  t: TranslateFn
): string {
  if (method === 'STRIPE') return t('paymentTypeStripe');
  if (method === 'ATH') return t('paymentTypeAth');
  return '—';
}

export function formatClientPaymentStatus(
  status: ClientPaymentStatus,
  t: TranslateFn
): string {
  if (status === 'pending') return t('paymentStatus_pending');
  if (status === 'confirmed') return t('paymentStatus_confirmed');
  return t('paymentStatus_unpaid');
}

export function formatBillingLabel(summary: JobPaymentSummary, t: TranslateFn): string {
  if (summary.paymentType === 'hourly') {
    const rate = summary.hourlyRate ?? 0;
    const hours = summary.totalHours ?? 0;
    return t('hourlyBreakdown', { rate, hours });
  }
  if (summary.paymentType === 'fixed') return t('fixedPrice');
  return '—';
}

export function getBillingAmount(job: JobWithPaymentSummary | null): number {
  if (!job) return 0;
  const summary = getPaymentSummary(job);
  if (summary.paymentType === 'hourly' && summary.hourlyRate && summary.totalHours) {
    return summary.hourlyRate * summary.totalHours;
  }
  return Number(job.budget) || 0;
}
